import type { ReactNode } from 'react';
import { useState } from 'react';

import { Toast } from '../Toast';
import type { ToastDispatcher, ToastObj, ToastOptions } from '../types';
import type { ToastContextValue } from './ToastContext';
import { ToastContext } from './ToastContext';

/**
 * Wrap the app with this context provider to enable the {@link ToastContext} for
 * the {@link useToast} hook.
 *
 * ```tsx
 * <ToastProvider>
 *   <App />
 * </ToastProvider>;
 * ```
 */
export function ToastProvider(props: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastObj>();
  const [toastOptions, setToastOptions] =
    useState<Omit<ToastOptions, 'isOpen'>>();

  const handleClose = () => {
    setToast(undefined);
    setToastOptions(undefined);
    toastOptions?.onClose?.();
  };
  const handleOpen: ToastDispatcher = (toastObj, options) => {
    setToast(toastObj);
    setToastOptions(options);
  };
  const value: ToastContextValue = {
    toast,
    openToast: handleOpen,
    closeToast: handleClose,
  };

  return (
    <ToastContext.Provider value={value}>
      {props.children}
      <Toast
        isOpen={!!toast}
        {...toast}
        {...toastOptions}
        onClose={handleClose}
      />
    </ToastContext.Provider>
  );
}
